import { Box } from 'reflexbox';
import styled from 'styled-components';
import key from 'weak-key';
import Moment from 'react-moment';

import { Link, useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

const LearningCalendarDayElement = styled(Box)<{ isSelectedMonth?: boolean }>`
	color: ${(props) => props.theme.colors.lightBlue};
	min-height: 100px;
	border-right: 1px solid ${(props) => props.theme.colors.lightBlue};

	:last-child {
		border-right-width: 0;
	}

	${(props) =>
		props.isSelectedMonth &&
		`
		color: ${props.theme.colors.slateBlue};
	`}
`;

const LearningCalendarEventElement = styled(Link)<{
	tintColor: string;
	isRSVP: boolean;
	cancelled: boolean;
}>`
	display: block;
	border-radius: 4px;
	border: 1px solid ${(props) => props.tintColor};
	font-size: 12px;
	padding: 3px 4px;
	margin-bottom: 5px;
	transition: 200ms transform ease-out, 200ms box-shadow ease-out;
	${(props) => (props.cancelled ? 'opacity: 0.4;' : '')}
	:last-child {
		margin-bottom: 0;
	}

	:hover {
		transform: translateY(-2px);
		box-shadow: 0 1px 3px 1px rgba(${(props) => toRgbString(props.tintColor)}, 0.4);
	}

	${(props) =>
		props.isRSVP
			? `
			background-color: ${props.tintColor};
			color: ${props.theme.colors.white};
		`
			: `
			background-color: ${props.theme.colors.white};
			color: ${props.tintColor};
	`}
`;

const LearningCalendarEventTime = styled.p`
	font-weight: 700;
	font-size: 10px;
	padding-bottom: 0;
	text-align: right;
	display: block;
	margin-bottom: -3px;
`;

const LearningEventTitle = styled.span<{ cancelled: boolean }>`
	${(props) => (props.cancelled ? 'text-decoration: line-through;' : '')}
`;

const LearningCalendarEvent = ({ event }: { event: graphql.LearningEvent }) => {
	const { localize } = useLocalize();
	const history = useHistory();

	const cancelled = event.status === graphql.LearningEventStatus.Cancelled;

	return (
		<LearningCalendarEventElement
			to={`/learning/event/${event.id}`}
			navigationState={{
				backgroundLocation: history.location
			}}
			tintColor={event.learningType.color}
			isRSVP={event.isViewerAttending}
			cancelled={cancelled}
		>
			<LearningEventTitle cancelled={cancelled}>{localize(event.title)?.text}</LearningEventTitle>
			<LearningCalendarEventTime>
				<Moment format="HH:mm">{event.startDate}</Moment>-
				<Moment format="HH:mm">{event.endDate}</Moment>
			</LearningCalendarEventTime>
		</LearningCalendarEventElement>
	);
};

const LearningCalendarDay = ({
	date,
	month,
	events,
}: {
	date: Date;
	month: number;
	events: graphql.LearningEvent[];
}) => {
	return (
		<LearningCalendarDayElement
			flexGrow={1}
			flexBasis={1}
			p={2}
			isSelectedMonth={date.getMonth() === month - 1}
		>
			{date.getDate()}

			{events.map((event) => (
				<LearningCalendarEvent key={key(event)} event={event} />
			))}
		</LearningCalendarDayElement>
	);
};

export default LearningCalendarDay;
